"use client";

import {
  CaretDown,
  CheckCircle,
  Circle,
  Clock,
  Wrench,
  XCircle,
} from "@phosphor-icons/react";
import type { DynamicToolUIPart, ToolUIPart } from "ai";
import type { ComponentProps, ReactNode } from "react";
import { isValidElement } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Icon } from "@/lib/icon";
import { cn } from "@/lib/utils";

export type ToolProps = ComponentProps<typeof Collapsible>;

export const Tool = ({ className, ...props }: ToolProps) => (
  <Collapsible
    className={cn(
      "group not-prose w-full rounded-md border border-border",
      className,
    )}
    {...props}
  />
);

export type ToolPart = ToolUIPart | DynamicToolUIPart;

export type ToolHeaderProps = {
  title?: string;
  className?: string;
} & (
  | { type: ToolUIPart["type"]; state: ToolUIPart["state"]; toolName?: never }
  | {
      type: DynamicToolUIPart["type"];
      state: DynamicToolUIPart["state"];
      toolName: string;
    }
);

const statusLabels: Record<ToolPart["state"], string> = {
  "approval-requested": "Awaiting Approval",
  "approval-responded": "Responded",
  "input-available": "Running",
  "input-streaming": "Pending",
  "output-available": "Completed",
  "output-denied": "Denied",
  "output-error": "Error",
};

const statusIcons: Record<ToolPart["state"], ReactNode> = {
  "approval-requested": (
    <Icon icon={Clock} className="size-4 text-yellow-600" />
  ),
  "approval-responded": (
    <Icon icon={CheckCircle} className="size-4 text-blue-600" />
  ),
  "input-available": <Icon icon={Clock} className="size-4 animate-pulse" />,
  "input-streaming": <Icon icon={Circle} className="size-4" />,
  "output-available": (
    <Icon icon={CheckCircle} className="size-4 text-green-600" />
  ),
  "output-denied": <Icon icon={XCircle} className="size-4 text-orange-600" />,
  "output-error": <Icon icon={XCircle} className="size-4 text-red-600" />,
};

export const getStatusBadge = (status: ToolPart["state"]) => (
  <Badge
    className="gap-1.5 rounded-full font-medium text-xs"
    variant="secondary"
  >
    {statusIcons[status]}
    {statusLabels[status]}
  </Badge>
);

export const ToolHeader = ({
  className,
  title,
  type,
  state,
  toolName,
  ...props
}: ToolHeaderProps) => {
  const derivedName =
    type === "dynamic-tool" ? toolName : type.split("-").slice(1).join("-");

  return (
    <CollapsibleTrigger
      className={cn("flex w-full items-center justify-between p-3", className)}
      {...props}
    >
      <div className="flex items-center gap-2">
        <Icon icon={Wrench} className="size-4 text-muted-foreground" />
        <span className="font-medium text-foreground text-sm">
          {title ?? derivedName}
        </span>
        {getStatusBadge(state)}
      </div>
      <Icon
        className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180"
        icon={CaretDown}
      />
    </CollapsibleTrigger>
  );
};

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>;

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
  <CollapsibleContent
    className={cn(
      "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 flex flex-col text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in",
      className,
    )}
    {...props}
  />
);

export type ToolInputProps = ComponentProps<"div"> & {
  input: ToolPart["input"];
};

export const ToolInput = ({ className, input, ...props }: ToolInputProps) => (
  <div
    className={cn("flex w-full flex-col gap-2 overflow-hidden p-4", className)}
    {...props}
  >
    <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
      Parameters
    </h4>
    <pre className="overflow-x-auto rounded-md bg-muted p-4 font-mono font-normal text-foreground text-xs leading-[18px]">
      {JSON.stringify(input, null, 2)}
    </pre>
  </div>
);

export type ToolOutputProps = ComponentProps<"div"> & {
  output: ToolPart["output"];
  errorText: ToolPart["errorText"];
};

export const ToolOutput = ({
  className,
  output,
  errorText,
  ...props
}: ToolOutputProps) => {
  if (!(output || errorText)) {
    return null;
  }

  let body: ReactNode = <div>{output as ReactNode}</div>;

  if (typeof output === "string") {
    body = (
      <pre className="whitespace-pre-wrap break-words font-mono font-normal text-sm leading-[20px]">
        {output}
      </pre>
    );
  } else if (typeof output === "object" && !isValidElement(output)) {
    body = (
      <pre className="whitespace-pre-wrap break-words font-mono font-normal text-xs leading-[18px]">
        {JSON.stringify(output, null, 2)}
      </pre>
    );
  }

  return (
    <div className={cn("flex w-full flex-col gap-2 p-4", className)} {...props}>
      <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
        {errorText ? "Error" : "Result"}
      </h4>
      {/* `font-normal` on the box wrapper too so weight 400 cascades to
          everything inside — guards against any parent context (the
          assistant message bubble, a `prose` container) trying to drag
          the result entries to Medium via the text-sm token bundle. */}
      <div
        className={cn(
          "overflow-x-auto rounded-md p-4 font-normal [&_table]:w-full",
          errorText
            ? "bg-destructive/10 text-destructive"
            : "bg-muted text-foreground",
        )}
      >
        {/* Render both when both present — upstream behaviour: an errored
            tool call can still emit a partial result and consumers should
            see both. Body is only rendered when there's actually an
            output so a pure-error case doesn't leave an empty wrapper. */}
        {errorText && <div>{errorText}</div>}
        {output != null && body}
      </div>
    </div>
  );
};
